import { usePropertyName } from '@vibesites/runtime/react';
import { clean } from './quinn';
import { useRawProperty } from './hooks';

const ICONS: Record<string, string[]> = {
  facebook: ['M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.6 1.6-1.6h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4.1 1.5-4.1 4.2v2.3H7.4V14h2.8v8h3.3Z'],
  instagram: ['M12 7.2A4.8 4.8 0 1 0 12 16.8 4.8 4.8 0 1 0 12 7.2Zm0 7.9a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2ZM17 5.9a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2ZM21.5 8.1c-.1-1.5-.4-2.8-1.5-3.9S17.6 2.7 16.1 2.6C14.6 2.5 9.4 2.5 7.9 2.6 6.4 2.7 5.1 3 4 4.1S2.7 6.4 2.6 7.9c-.1 1.5-.1 6.7 0 8.2.1 1.5.4 2.8 1.5 3.9s2.4 1.4 3.9 1.5c1.5.1 6.7.1 8.2 0 1.5-.1 2.8-.4 3.9-1.5s1.4-2.4 1.5-3.9c.1-1.5.1-6.7-.1-8Zm-2 10c-.3.8-1 1.5-1.8 1.8-1.2.5-4.2.4-5.7.4s-4.5.1-5.7-.4c-.8-.3-1.5-1-1.8-1.8-.5-1.2-.4-4.2-.4-5.7s-.1-4.5.4-5.7c.3-.8 1-1.5 1.8-1.8 1.2-.5 4.2-.4 5.7-.4s4.5-.1 5.7.4c.8.3 1.5 1 1.8 1.8.5 1.2.4 4.2.4 5.7s.1 4.5-.4 5.7Z'],
  tiktok: ['M16.6 2h-3.4v13.6a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .9.1V9.4a6.3 6.3 0 1 0 5.4 6.2V8.8a7.4 7.4 0 0 0 4.4 1.4V6.8A4.4 4.4 0 0 1 16.6 2Z'],
  youtube: ['M23 7.2c-.3-1-1-1.8-2-2C19.2 4.7 12 4.7 12 4.7s-7.2 0-9 .5c-1 .2-1.7 1-2 2C.5 9 .5 12 .5 12s0 3 .5 4.8c.3 1 1 1.8 2 2 1.8.5 9 .5 9 .5s7.2 0 9-.5c1-.2 1.7-1 2-2 .5-1.8.5-4.8.5-4.8s0-3-.5-4.8ZM9.7 15.2V8.8l6 3.2-6 3.2Z'],
  linkedin: ['M6.9 21H3.3V9h3.6v12ZM5.1 7.4a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2ZM21 21h-3.6v-5.8c0-1.4 0-3.2-1.9-3.2s-2.2 1.5-2.2 3.1V21H9.7V9h3.5v1.6h.1c.5-.9 1.7-1.9 3.4-1.9 3.7 0 4.3 2.4 4.3 5.5V21Z'],
  google: [
    'M21.6 12.2c0-.7-.1-1.3-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3Z',
    'M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z',
    'M6.4 13.9A6 6 0 0 1 6.1 12c0-.7.1-1.3.3-1.9V7.5H3.1a10 10 0 0 0 0 9l3.3-2.6Z',
    'M12 6c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6Z',
  ],
};

const SOCIALS: Array<[string, string, string]> = [
  ['facebook', 'property_facebook_url', 'Facebook'],
  ['instagram', 'property_instagram_url', 'Instagram'],
  ['tiktok', 'property_tiktok_url', 'TikTok'],
  ['youtube', 'property_youtube_url', 'YouTube'],
  ['linkedin', 'property_linkedin_url', 'LinkedIn'],
  ['google', 'property_google_business_url', 'Google Business Profile'],
];

/** The property's social profiles as icon links; renders nothing when it has none. */
export function SocialLinks({ className }: { className: string }) {
  const p = useRawProperty();
  const name = usePropertyName();
  const links = SOCIALS.map(([key, field, label]) => ({ key, label, href: clean(p[field]) })).filter((l) => l.href);
  if (!links.length) return null;
  return (
    <div className={`social ${className}`}>
      {links.map((l) => (
        <a key={l.key} href={l.href!} target="_blank" rel="noopener" aria-label={`${name} on ${l.label}`}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            {ICONS[l.key].map((d, i) => (
              <path key={i} d={d} />
            ))}
          </svg>
        </a>
      ))}
    </div>
  );
}
